import type { Locale } from "@/i18n/languages";
import { isEuCountry } from "@/lib/customer/vat";
import { countryPreset, NOTE_COUNTRIES } from "./country-presets";
import type { VatMode } from "./vat-modes";

type NoteMode = Exclude<VatMode, "standard">;

/**
 * The sentence printed under the totals when an invoice charges no VAT, in the document's
 * language (German and English for now; other languages get the English one). It is only a
 * starting point: the editor lets the user change it, and the exact wording is the user's
 * responsibility (country presets refine it).
 */
export const VAT_NOTES: Partial<Record<Locale, Record<NoteMode, string>>> = {
  en: {
    small_business: "No VAT is charged under the small business scheme.",
    reverse_charge: "Reverse charge: VAT is to be accounted for by the recipient.",
    intra_eu: "VAT-exempt intra-Community supply.",
    export: "Not subject to VAT here: the place of supply is outside the EU.",
    exempt: "VAT-exempt supply.",
  },
  de: {
    small_business: "Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.",
    reverse_charge: "Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge).",
    intra_eu: "Steuerfreie innergemeinschaftliche Lieferung.",
    export: "Nicht im Inland steuerbare Leistung.",
    exempt: "Umsatzsteuerfreie Leistung.",
  },
};

/**
 * The export note of a seller outside the EU without its own wording: the place of supply is
 * outside their country, not outside the EU. {country} is the country's name.
 */
export const EXPORT_OUTSIDE: Partial<Record<Locale, string>> = {
  en: "No VAT ({country}): the place of supply is abroad.",
  de: "Keine Umsatzsteuer ({country}): Der Leistungsort liegt im Ausland.",
};

function regionName(code: string, language: Locale): string {
  try {
    return new Intl.DisplayNames([language, "en"], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/**
 * The note for a VAT mode in `language`: the seller country's legal wording when its preset has
 * one in that language (DE, AT, CH, GB, GE, AE, MT, NL), else the generic note (English if there
 * is none), naming the seller's own country for exports from outside the EU; "" for standard VAT.
 */
export function vatNoteFor(mode: VatMode, language: Locale, country?: string): string {
  if (mode === "standard") return "";
  const own = countryPreset(country)?.notes[mode]?.[language];
  if (own) return own;
  if (mode === "export" && country && /^[A-Z]{2}$/.test(country) && !isEuCountry(country)) {
    const template = EXPORT_OUTSIDE[language] ?? EXPORT_OUTSIDE.en!;
    return template.replace("{country}", regionName(country, language));
  }
  return (VAT_NOTES[language] ?? VAT_NOTES.en!)[mode];
}

const escape = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const EXPORT_PATTERNS = Object.values(EXPORT_OUTSIDE).map(
  (template) => new RegExp(`^${escape(template).replace(escape("{country}"), ".+")}$`),
);

/** Whether `note` is one of the preset notes (so switching the mode may replace it). */
export function isPresetVatNote(note: string): boolean {
  const generic = Object.values(VAT_NOTES).flatMap((notes) => Object.values(notes));
  const countries = NOTE_COUNTRIES.flatMap((code) =>
    Object.values(countryPreset(code)?.notes ?? {}).flatMap((byLanguage) =>
      Object.values(byLanguage ?? {}),
    ),
  );
  return [...generic, ...countries].includes(note) || EXPORT_PATTERNS.some((p) => p.test(note));
}
